import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppConfig, ConfigService, logger } from '@icc/shared';
import * as Sentry from '@sentry/angular-ivy';
import { environment } from '../environments/environment';
import { B2BConfig, Panel } from '@icc/shared/types';

declare global {
    interface Window {
        showAppError: (error?: any) => void;
        hideAppError: () => void;
        setErrorPageLanguage: (lang: string) => void;
    }
}

@Injectable({
    providedIn: 'root',
})
export class InitService {
    options: B2BConfig | undefined;

    constructor(
        private httpClient: HttpClient,
        private permissionsService: NgxPermissionsService,
        private configService: ConfigService,
    ) {}

    loadOptionsFromServer() {
        return new Promise<void>((resolve, reject) => {
            this.requestOptions()
                .toPromise()
                .then((data) => {
                    this.options = data;
                    this.configService.config = this.options;
                    this.configService.activePanel = this.options.userData?.user?.ActivePanel as Panel;
                    const permissions = data.userData?.permissions;

                    this.setSentry();

                    if (!permissions) {
                        window.location.href = window.location.origin + '/iccadmin' + '?redirect=' + window.location.pathname;
                        reject();
                    } else {

                        this.importAngularLocale(data.languageCode).then(() => {
                            const permissionArray: string[] = Object.values(permissions);

                            this.permissionsService.flushPermissions();
                            this.permissionsService.addPermission(permissionArray);

                            this.permissionsService
                                .hasPermission(['access-trade', 'access-customer', 'access-complaintb2c'])
                                .then((hasPermissions) => {
                                    if (hasPermissions) {
                                        resolve();
                                    } else {
                                        window.location.href = window.location.origin + '/iccadmin' + '?redirect=' + window.location.pathname;
                                        reject();
                                    }
                                })
                                .catch((error) => {
                                    window.location.href = window.location.origin + '/iccadmin' + '?redirect=' + window.location.pathname;
                                    reject();
                                });
                        });
                    }
                })
                .catch((error) => {
                    logger.error('Failed to load options from server:', error);
                    this.showErrorPage(error);
                    reject(error);
                });
        });
    }

    /**
     * Wyświetla statyczną stronę błędu gdy aplikacja nie może się zainicjalizować
     */
    private showErrorPage(error?: any): void {
        if (typeof window.showAppError === 'function') {
            window.showAppError(error);
        }
    }


    getAppConfig(): AppConfig {
        if (this.options) {
            return {
                IccConfig: this.options.config,
                EnvConfig: this.options.env,
                CurLang: this.options.languageCode || 'en',
            };
        }
        return null;
    }

    setSentry() {
        if (this.options.env.sentryDsn) {
            Sentry
                .init({
                    dsn: this.options.env.sentryDsn,
                    environment: this.options.env.sentryEnv,
                    release: environment?.appVersion,
                    integrations: [
                        Sentry.browserTracingIntegration(),
                    ],
                    tracesSampleRate: 1.0,
                });
            if (this.options.userData?.user) {
                Sentry.setUser({
                    id: this.options.userData?.user.id,
                    email: this.options.userData?.user.email,
                });
            }
            Sentry.setTags({
                lang: this.options.languageCode,
                market: this.configService.user?.user?.ActiveMarket,
                company: this.configService.user?.user?.ActiveCompany,
            });
        }

    }

    private requestOptions() {
        return this.httpClient.post<any>('/api/b2b/options', {});
    }

    private importAngularLocale(locale: string) {
        return import(`node_modules/@angular/common/locales/global/${locale}.js`).catch((error) => {
            locale = locale.split('-')[0];
            return import(`node_modules/@angular/common/locales/global/${locale}.js`);
        }).catch((error) => {
            logger.warn('Locale not found, using en');
            return import('node_modules/@angular/common/locales/global/en.js');
        });
    }
}
